/*
 * Copyright (c) Nexign, JSC, 2023.
 */
/**
 * News manage delete news confirm
 *
 * @author Evgeniy Naumov
 * @version 1.26.0
 * @since 1.25.1
 */

import type { FC } from 'react';

import { Button } from '@nexign/scc-ui-kit';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';

import EFormValidation from '@/main/modules/form/const/formValidation';
import { IApiError } from '@/main/types/globalTypes';
import { icons } from '@assets/index';
import { ImgStyled } from '@components/common/Block/Img';
import { makeSelectPageNewsTranslation, makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import Modal from '@modules/modal/components/Modal';
import { ModalTitleStyled, ModalTitleTextStyled } from '@modules/modal/components/styled/ModalStyled';
import { EModalStatus } from '@modules/modal/types/modalTypes';
import { stringFormatParams } from '@utils/stringFormat';

import { EFormatData } from '../../../PageDashboard/types/widgetTypes';
import { ECreateNewsTypes } from '../../constants';
import { IActionModalParams } from '../../types';
import { validatePublishError } from '../../validation';
import { useActionModalTranslation } from './hooks/useActionModalTranslation';

interface IProps {
    type: ECreateNewsTypes;
    params?: IActionModalParams;
    onCloseModal: () => void;
    handleCreate?: () => void;
    apiError?: IApiError;
}

export const NewsActionModal: FC<IProps> = ({ type, params, handleCreate, onCloseModal, apiError }) => {
    const tNews = useSelector(makeSelectPageNewsTranslation());
    const tCorporate = useSelector(makeSelectTranslationCommon());

    const {
        createNews: {
            form: { controls: tControls },
        },
        modals: {
            publishModals: { params: tParams },
        },
    } = tNews;

    const modalTranslation = useActionModalTranslation(type);

    const error = apiError ? validatePublishError(tCorporate.form, tNews, apiError.code as EFormValidation) : null;

    return (
        <Modal dataName="news-item-create-external-modal" style={{ padding: '24px' }} width="480px">
            <Modal.Title className="modal-popup-title__base">
                <ModalTitleStyled>
                    <ModalTitleTextStyled>{modalTranslation.title}</ModalTitleTextStyled>
                    <ImgStyled height="16px" src={icons.cancelSmallIcon} width="16px" onClick={onCloseModal} />
                </ModalTitleStyled>
            </Modal.Title>
            <Modal.Status status={EModalStatus.start}>
                <Modal.Text>{modalTranslation.start}</Modal.Text>
                <Modal.Text>
                    {stringFormatParams(tParams.title, {
                        title: params?.title,
                    })}
                </Modal.Text>
                <Modal.Text>
                    {stringFormatParams(tParams.publishFrom, {
                        date: dayjs(params?.publishFrom).format(EFormatData.FullData),
                    })}
                </Modal.Text>
                {params?.publishTo && (
                    <Modal.Text>
                        {stringFormatParams(tParams.publishTo, {
                            date: dayjs(params.publishTo).format(EFormatData.FullData),
                        })}
                    </Modal.Text>
                )}
                <Modal.Control className="modal-popup-control__base margin-between">
                    {handleCreate && (
                        <Button size="small" typographyProps={{ variant: 'h5' }} onClick={handleCreate}>
                            {tControls.submit}
                        </Button>
                    )}
                    <Button color="info" size="small" variant="text" onClick={onCloseModal}>
                        {tControls.cancel}
                    </Button>
                </Modal.Control>
            </Modal.Status>
            <Modal.Status status={EModalStatus.success}>
                <Modal.Text>{modalTranslation.success}</Modal.Text>
            </Modal.Status>
            <Modal.Status status={EModalStatus.failure}>
                <Modal.Text>{modalTranslation.error}</Modal.Text>
                {!!error && <Modal.Text>{error}</Modal.Text>}
            </Modal.Status>
        </Modal>
    );
};
